/**
 * Predictive Safety Monitor
 *
 * Advanced real-time safety monitoring with predictive analytics for:
 * - Patient safety risk prediction
 * - Financial fraud pattern detection
 * - Legal privilege breach prevention
 * - System failure prediction
 * - Regulatory compliance drift detection
 */

import React, { createContext, useContext, useEffect, useState } from 'react';

// FERP Safety Framework Integration (Required by FERP standards)

// Predictive Analytics Types
export interface SafetyMetric {
  id: string;
  domain: 'HEALTHCARE' | 'FINANCIAL' | 'LEGAL' | 'OPERATIONAL';
  type: string;
  currentValue: number;
  predictedValue: number;
  trend: 'IMPROVING' | 'STABLE' | 'DEGRADING' | 'CRITICAL';
  confidence: number; // 0-100%
  timestamp: number;
  riskScore: number; // 0-100
  thresholds: {
    warning: number;
    critical: number;
    emergency: number;
  };
}

export interface PredictiveAlert {
  id: string;
  type: 'PREDICTIVE_WARNING' | 'TREND_ALERT' | 'ANOMALY_DETECTED' | 'EMERGENCY_PREDICTED';
  domain: 'HEALTHCARE' | 'FINANCIAL' | 'LEGAL' | 'OPERATIONAL';
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  description: string;
  predictedImpact: string;
  timeToImpact: number; // milliseconds
  confidence: number;
  suggestedActions: string[];
  metadata: Record<string, any>;
  createdAt: number;
  acknowledged: boolean;
}

export interface SafetyPattern {
  id: string;
  domain: string;
  patternType: 'SEASONAL' | 'CYCLICAL' | 'TRENDING' | 'ANOMALOUS';
  description: string;
  frequency: number; // occurrences per time period
  strength: number; // 0-1 pattern strength
  lastOccurrence: number;
  nextPredicted: number;
  associatedRisks: string[];
}

export interface RiskPrediction {
  riskType: string;
  domain: string;
  probability: number; // 0-1
  severity: number; // 0-10
  timeframe: number; // milliseconds until predicted occurrence
  confidence: number; // 0-1
  contributingFactors: string[];
  mitigationStrategies: string[];
}

// Machine Learning Models for Prediction
class SafetyPredictionEngine {
  private metricHistory: Map<string, SafetyMetric[]> = new Map();
  private patterns: Map<string, SafetyPattern> = new Map();
  private modelWeights: Map<string, number[]> = new Map();

  constructor() {
    this.initializeModels();
  }

  /**
   * Initialize ML models for different domains
   */
  private initializeModels(): void {
    // Healthcare prediction models
    this.modelWeights.set('HEALTHCARE_PATIENT_RISK', [
      0.3, // Vital signs trend
      0.25, // Medication compliance
      0.2, // Historical complications
      0.15, // Age/risk factors
      0.1, // Environmental factors
    ]);

    // Financial fraud detection models
    this.modelWeights.set('FINANCIAL_FRAUD_RISK', [
      0.35, // Transaction patterns
      0.25, // Velocity anomalies
      0.2, // Geographic anomalies
      0.15, // Time-based patterns
      0.05, // Account age factors
    ]);

    // Legal privilege risk models
    this.modelWeights.set('LEGAL_PRIVILEGE_RISK', [
      0.4, // Document classification confidence
      0.3, // Recipient analysis
      0.2, // Context awareness
      0.1, // Historical breaches
    ]);
  }

  /**
   * Add metric data point for prediction
   */
  public addMetricData(metric: SafetyMetric): void {
    const key = `${metric.domain}_${metric.type}`;

    if (!this.metricHistory.has(key)) {
      this.metricHistory.set(key, []);
    }

    const history = this.metricHistory.get(key)!;
    history.push(metric);

    // Keep only last 1000 data points for performance
    if (history.length > 1000) {
      history.shift();
    }

    // Update predictions based on new data
    this.updatePredictions(key, history);
  }

  /**
   * Update predictions based on historical data
   */
  private updatePredictions(key: string, history: SafetyMetric[]): void {
    if (history.length < 10) return; // Need minimum data for predictions

    const latest = history[history.length - 1];

    // Calculate trend using linear regression
    const trend = this.calculateTrend(history.slice(-20)); // Last 20 points

    // Detect anomalies
    const anomaly = latest ? this.detectAnomaly(history.slice(-50), latest) : null; // Last 50 points

    // Update metric predictions
    if (latest) {
      latest.predictedValue = this.predictNextValue(history);
      latest.trend = this.classifyTrend(trend);
      latest.confidence = this.calculateConfidence(history);
    }

    // Detect patterns
    this.updatePatterns(key, history);
  }

  /**
   * Calculate trend using simple linear regression
   */
  private calculateTrend(data: SafetyMetric[]): number {
    if (data.length < 2) return 0;

    const n = data.length;
    const sumX = data.reduce((sum, _, index) => sum + index, 0);
    const sumY = data.reduce((sum, metric) => sum + metric.currentValue, 0);
    const sumXY = data.reduce((sum, metric, index) => sum + index * metric.currentValue, 0);
    const sumX2 = data.reduce((sum, _, index) => sum + index * index, 0);

    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    return slope;
  }

  /**
   * Detect anomalies using statistical methods
   */
  private detectAnomaly(history: SafetyMetric[], current: SafetyMetric): boolean {
    if (history.length < 10) return false;

    const values = history.map((m) => m.currentValue);
    const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
    const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
    const stdDev = Math.sqrt(variance);

    // Consider anomaly if current value is more than 3 standard deviations from mean
    const zScore = Math.abs((current.currentValue - mean) / stdDev);
    return zScore > 3;
  }

  /**
   * Predict next value using weighted moving average and trend
   */
  private predictNextValue(history: SafetyMetric[]): number {
    if (history.length < 3) return history[history.length - 1]?.currentValue ?? 0;

    const recent = history.slice(-10); // Last 10 points
    const weights = recent.map((_: any, index) => index + 1); // Linear weights
    const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);

    const weightedAverage =
      recent.reduce((sum, metric, index) => {
        return sum + metric.currentValue * (weights[index] ?? 1);
      }, 0) / totalWeight;

    const trend = this.calculateTrend(recent);
    return weightedAverage + trend;
  }

  /**
   * Classify trend direction
   */
  private classifyTrend(slope: number): 'IMPROVING' | 'STABLE' | 'DEGRADING' | 'CRITICAL' {
    if (slope > 0.5) return 'IMPROVING';
    if (slope < -0.5) return 'DEGRADING';
    if (slope < -2) return 'CRITICAL';
    return 'STABLE';
  }

  /**
   * Calculate prediction confidence
   */
  private calculateConfidence(history: SafetyMetric[]): number {
    if (history.length < 5) return 50; // Low confidence with little data

    // Calculate prediction accuracy over last 10 predictions
    const predictions = history.slice(-10);
    let accurateCount = 0;

    for (let i = 1; i < predictions.length; i++) {
      const predicted = predictions[i - 1]?.predictedValue ?? 0;
      const actual = predictions[i]?.currentValue ?? 0;
      const error = Math.abs((predicted - actual) / actual);

      if (error < 0.1) accurateCount++; // 10% error tolerance
    }

    return Math.min(95, Math.max(10, (accurateCount / (predictions.length - 1)) * 100));
  }

  /**
   * Update pattern detection
   */
  private updatePatterns(key: string, history: SafetyMetric[]): void {
    // Detect cyclical patterns (daily, weekly, monthly)
    this.detectCyclicalPatterns(key, history);

    // Detect seasonal patterns
    this.detectSeasonalPatterns(key, history);

    // Detect trending patterns
    this.detectTrendingPatterns(key, history);
  }

  /**
   * Detect cyclical patterns
   */
  private detectCyclicalPatterns(key: string, history: SafetyMetric[]): void {
    const cycles = [
      { name: 'HOURLY', period: 60 * 60 * 1000 },
      { name: 'DAILY', period: 24 * 60 * 60 * 1000 },
      { name: 'WEEKLY', period: 7 * 24 * 60 * 60 * 1000 },
    ];

    cycles.forEach((cycle) => {
      const correlation = this.calculateCyclicalCorrelation(history, cycle.period);

      if (correlation > 0.7) {
        // Strong correlation
        const patternId = `${key}_${cycle.name}`;
        this.patterns.set(patternId, {
          id: patternId,
          domain: key.split('_')[0] || 'unknown',
          patternType: 'CYCLICAL',
          description: `${cycle.name} pattern detected in ${key}`,
          frequency: cycle.period,
          strength: correlation,
          lastOccurrence: Date.now(),
          nextPredicted: Date.now() + cycle.period,
          associatedRisks: this.getAssociatedRisks(key, cycle.name),
        });
      }
    });
  }

  /**
   * Calculate cyclical correlation
   */
  private calculateCyclicalCorrelation(history: SafetyMetric[], period: number): number {
    if (history.length < period / (60 * 1000)) return 0; // Need at least one cycle

    // Group data by cycle position
    const cycles: number[][] = [];
    const cycleLength = Math.floor(period / (60 * 1000)); // Convert to data points

    for (let i = 0; i < history.length - cycleLength; i += cycleLength) {
      const cycle = history.slice(i, i + cycleLength).map((m) => m.currentValue);
      if (cycle.length === cycleLength) {
        cycles.push(cycle);
      }
    }

    if (cycles.length < 2) return 0;

    // Calculate correlation between cycles
    let totalCorrelation = 0;
    let comparisons = 0;

    for (let i = 0; i < cycles.length - 1; i++) {
      for (let j = i + 1; j < cycles.length; j++) {
        if (cycles[i] && cycles[j]) {
          totalCorrelation += this.pearsonCorrelation(cycles[i]!, cycles[j]!);
        }
        comparisons++;
      }
    }

    return comparisons > 0 ? totalCorrelation / comparisons : 0;
  }

  /**
   * Calculate Pearson correlation coefficient
   */
  private pearsonCorrelation(x: number[], y: number[]): number {
    if (x.length !== y.length || x.length === 0) return 0;

    const n = x.length;
    const sumX = x.reduce((sum, val) => sum + val, 0);
    const sumY = y.reduce((sum, val) => sum + val, 0);
    const sumXY = x.reduce((sum, val, i) => sum + val * (y[i] ?? 0), 0);
    const sumX2 = x.reduce((sum, val) => sum + val * val, 0);
    const sumY2 = y.reduce((sum, val) => sum + val * val, 0);

    const numerator = n * sumXY - sumX * sumY;
    const denominator = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));

    return denominator === 0 ? 0 : numerator / denominator;
  }

  /**
   * Detect seasonal patterns
   */
  private detectSeasonalPatterns(key: string, history: SafetyMetric[]): void {
    // Implementation for seasonal pattern detection
    // Would use more sophisticated time series analysis
  }

  /**
   * Detect trending patterns
   */
  private detectTrendingPatterns(key: string, history: SafetyMetric[]): void {
    if (history.length < 20) return;

    const recentTrend = this.calculateTrend(history.slice(-20));
    const longTermTrend = this.calculateTrend(history);

    if (Math.abs(recentTrend) > 1 || Math.abs(longTermTrend) > 0.5) {
      const patternId = `${key}_TRENDING`;
      this.patterns.set(patternId, {
        id: patternId,
        domain: key.split('_')[0] || 'unknown',
        patternType: 'TRENDING',
        description: `Trending pattern in ${key}: ${recentTrend > 0 ? 'increasing' : 'decreasing'}`,
        frequency: 0,
        strength: Math.abs(recentTrend),
        lastOccurrence: Date.now(),
        nextPredicted: Date.now() + 24 * 60 * 60 * 1000, // Next day
        associatedRisks: this.getAssociatedRisks(key, 'TRENDING'),
      });
    }
  }

  /**
   * Get associated risks for pattern type
   */
  private getAssociatedRisks(key: string, patternType: string): string[] {
    const domain = key.split('_')[0];
    const risks: Record<string, string[]> = {
      HEALTHCARE_DAILY: ['MEDICATION_ERRORS', 'SHIFT_CHANGE_RISKS', 'FATIGUE_FACTORS'],
      HEALTHCARE_WEEKLY: ['WEEKEND_STAFFING', 'SCHEDULED_PROCEDURES'],
      FINANCIAL_DAILY: ['TRADING_HOURS', 'SETTLEMENT_RISKS'],
      FINANCIAL_WEEKLY: ['WEEKEND_FRAUD', 'MARKET_CLOSURE_RISKS'],
      LEGAL_TRENDING: ['INCREASING_PRIVILEGE_RISKS', 'DOCUMENT_VOLUME_RISKS'],
    };

    return risks[`${domain}_${patternType}`] || ['UNKNOWN_RISK_PATTERN'];
  }

  /**
   * Generate risk predictions
   */
  public generateRiskPredictions(domain: string): RiskPrediction[] {
    const predictions: RiskPrediction[] = [];
    const domainPatterns = Array.from(this.patterns.values()).filter(
      (pattern) => pattern.domain === domain,
    );

    domainPatterns.forEach((pattern) => {
      const prediction: RiskPrediction = {
        riskType: pattern.description,
        domain: pattern.domain,
        probability: pattern.strength,
        severity: this.calculatePatternSeverity(pattern),
        timeframe: pattern.nextPredicted - Date.now(),
        confidence: pattern.strength,
        contributingFactors: pattern.associatedRisks,
        mitigationStrategies: this.getMitigationStrategies(pattern),
      };

      predictions.push(prediction);
    });

    return predictions.sort((a, b) => b.probability * b.severity - a.probability * a.severity);
  }

  /**
   * Calculate pattern severity
   */
  private calculatePatternSeverity(pattern: SafetyPattern): number {
    const severityMap = {
      HEALTHCARE: 9, // Highest severity - patient safety
      FINANCIAL: 7, // High severity - financial impact
      LEGAL: 8, // Very high - legal consequences
      OPERATIONAL: 5, // Medium - operational impact
    };

    return severityMap[pattern.domain as keyof typeof severityMap] || 5;
  }

  /**
   * Get mitigation strategies
   */
  private getMitigationStrategies(pattern: SafetyPattern): string[] {
    const strategies: Record<string, string[]> = {
      HEALTHCARE: [
        'INCREASE_MONITORING_FREQUENCY',
        'ENHANCE_STAFF_ALERTNESS',
        'IMPLEMENT_DOUBLE_CHECKS',
        'ACTIVATE_CLINICAL_DECISION_SUPPORT',
      ],
      FINANCIAL: [
        'INCREASE_FRAUD_MONITORING',
        'IMPLEMENT_VELOCITY_LIMITS',
        'ENHANCE_TRANSACTION_VERIFICATION',
        'ACTIVATE_REAL_TIME_ANALYSIS',
      ],
      LEGAL: [
        'IMPLEMENT_ADDITIONAL_PRIVILEGE_CHECKS',
        'ENHANCE_DOCUMENT_CLASSIFICATION',
        'INCREASE_REVIEW_REQUIREMENTS',
        'ACTIVATE_ETHICS_MONITORING',
      ],
      OPERATIONAL: [
        'INCREASE_SYSTEM_MONITORING',
        'IMPLEMENT_REDUNDANCY',
        'ENHANCE_ERROR_DETECTION',
        'ACTIVATE_BACKUP_PROCEDURES',
      ],
    };

    return strategies[pattern.domain] || ['STANDARD_MONITORING'];
  }

  /**
   * Get all detected patterns
   */
  public getPatterns(): SafetyPattern[] {
    return Array.from(this.patterns.values());
  }

  /**
   * Get metric history
   */
  public getMetricHistory(domain: string, type: string): SafetyMetric[] {
    const key = `${domain}_${type}`;
    return this.metricHistory.get(key) || [];
  }
}

// Main Predictive Safety Monitor Class
export class PredictiveSafetyMonitor {
  private engine: SafetyPredictionEngine;
  private alerts: Map<string, PredictiveAlert> = new Map();
  private monitoringInterval?: NodeJS.Timeout;
  private alertCallbacks: ((alert: PredictiveAlert) => void)[] = [];
  private alertCounter = 0;
  private metricCounter = 0;

  constructor() {
    this.engine = new SafetyPredictionEngine();
    this.startMonitoring();
  }

  /**
   * Start continuous monitoring
   */
  private startMonitoring(): void {
    this.monitoringInterval = setInterval(() => {
      this.performPredictiveAnalysis();
    }, 5000); // Analyze every 5 seconds
  }

  /**
   * Perform predictive analysis
   */
  private async performPredictiveAnalysis(): Promise<void> {
    // Simulate incoming metrics data
    const domains: Array<'HEALTHCARE' | 'FINANCIAL' | 'LEGAL' | 'OPERATIONAL'> = [
      'HEALTHCARE',
      'FINANCIAL',
      'LEGAL',
      'OPERATIONAL',
    ];

    domains.forEach((domain) => {
      this.generateSyntheticMetrics(domain);
    });

    // Generate risk predictions
    domains.forEach((domain) => {
      const predictions = this.engine.generateRiskPredictions(domain);
      this.processPredictions(predictions);
    });

    // Check for anomalies and generate alerts
    this.generatePredictiveAlerts();
  }

  /**
   * Generate synthetic metrics for demonstration
   */
  private generateSyntheticMetrics(
    domain: 'HEALTHCARE' | 'FINANCIAL' | 'LEGAL' | 'OPERATIONAL',
  ): void {
    const metricTypes = {
      HEALTHCARE: ['PATIENT_RISK_SCORE', 'MEDICATION_ADHERENCE', 'VITAL_SIGNS_STABILITY'],
      FINANCIAL: ['FRAUD_RISK_SCORE', 'TRANSACTION_VELOCITY', 'ACCOUNT_ANOMALY_SCORE'],
      LEGAL: ['PRIVILEGE_RISK_SCORE', 'DOCUMENT_SENSITIVITY', 'DISCLOSURE_RISK'],
      OPERATIONAL: ['SYSTEM_HEALTH_SCORE', 'PERFORMANCE_METRICS', 'ERROR_RATE'],
    };

    metricTypes[domain].forEach((type) => {
      const baseValue = this.getBaseValue(domain, type);
      const noise = (Math.random() - 0.5) * 10; // Add some noise
      const trend = this.getTrendComponent(domain, type);

      const currentValue = Math.max(0, Math.min(100, baseValue + noise + trend));

      const metric: SafetyMetric = {
        id: `${domain}_${type}_${++this.metricCounter}`,
        domain,
        type,
        currentValue,
        predictedValue: currentValue, // Will be updated by engine
        trend: 'STABLE', // Will be updated by engine
        confidence: 75,
        timestamp: Date.now(),
        riskScore: this.calculateRiskScore(currentValue, domain),
        thresholds: this.getThresholds(domain, type),
      };

      this.engine.addMetricData(metric);
    });
  }

  /**
   * Get base value for metric type
   */
  private getBaseValue(domain: string, type: string): number {
    const baseValues: Record<string, number> = {
      HEALTHCARE_PATIENT_RISK_SCORE: 25,
      HEALTHCARE_MEDICATION_ADHERENCE: 85,
      HEALTHCARE_VITAL_SIGNS_STABILITY: 90,
      FINANCIAL_FRAUD_RISK_SCORE: 15,
      FINANCIAL_TRANSACTION_VELOCITY: 70,
      FINANCIAL_ACCOUNT_ANOMALY_SCORE: 20,
      LEGAL_PRIVILEGE_RISK_SCORE: 10,
      LEGAL_DOCUMENT_SENSITIVITY: 60,
      LEGAL_DISCLOSURE_RISK: 15,
      OPERATIONAL_SYSTEM_HEALTH_SCORE: 95,
      OPERATIONAL_PERFORMANCE_METRICS: 80,
      OPERATIONAL_ERROR_RATE: 5,
    };

    return baseValues[`${domain}_${type}`] || 50;
  }

  /**
   * Get trend component for realistic data
   */
  private getTrendComponent(domain: string, type: string): number {
    const time = Date.now();
    const hourOfDay = new Date(time).getHours();

    // Simulate daily patterns
    if (domain === 'HEALTHCARE') {
      // Higher risk during shift changes (7am, 3pm, 11pm)
      const shiftChangeHours = [7, 15, 23];
      if (shiftChangeHours.some((hour) => Math.abs(hourOfDay - hour) < 1)) {
        return 5; // Increase risk during shift changes
      }
    }

    if (domain === 'FINANCIAL') {
      // Higher activity during business hours
      if (hourOfDay >= 9 && hourOfDay <= 17) {
        return 3; // Increase activity during business hours
      }
    }

    return 0;
  }

  /**
   * Calculate risk score
   */
  private calculateRiskScore(value: number, domain: string): number {
    const riskMultipliers = {
      HEALTHCARE: 1.2, // Higher risk weight for healthcare
      FINANCIAL: 1.0,
      LEGAL: 1.1,
      OPERATIONAL: 0.8,
    };

    const multiplier = riskMultipliers[domain as keyof typeof riskMultipliers] || 1.0;
    return Math.min(100, value * multiplier);
  }

  /**
   * Get thresholds for metric type
   */
  private getThresholds(
    domain: string,
    type: string,
  ): { warning: number; critical: number; emergency: number } {
    const defaultThresholds = {
      HEALTHCARE: { warning: 70, critical: 85, emergency: 95 },
      FINANCIAL: { warning: 75, critical: 90, emergency: 98 },
      LEGAL: { warning: 60, critical: 80, emergency: 95 },
      OPERATIONAL: { warning: 80, critical: 90, emergency: 95 },
    };

    return (
      defaultThresholds[domain as keyof typeof defaultThresholds] || {
        warning: 70,
        critical: 85,
        emergency: 95,
      }
    );
  }

  /**
   * Process risk predictions
   */
  private processPredictions(predictions: RiskPrediction[]): void {
    predictions.forEach((prediction) => {
      if (prediction.probability > 0.7 && prediction.severity > 7) {
        this.createPredictiveAlert(prediction);
      }
    });
  }

  /**
   * Create predictive alert
   */
  private createPredictiveAlert(prediction: RiskPrediction): void {
    const alertId = `temp_${++this.alertCounter}`; // Centralized predictive alert ID generation

    const alert: PredictiveAlert = {
      id: alertId,
      type: 'PREDICTIVE_WARNING',
      domain: prediction.domain as any,
      severity: prediction.severity > 8 ? 'CRITICAL' : 'HIGH',
      description: `Predicted risk: ${prediction.riskType}`,
      predictedImpact: `${Math.round(prediction.probability * 100)}% probability in ${Math.round(prediction.timeframe / (60 * 1000))} minutes`,
      timeToImpact: prediction.timeframe,
      confidence: Math.round(prediction.confidence * 100),
      suggestedActions: prediction.mitigationStrategies,
      metadata: {
        probability: prediction.probability,
        severity: prediction.severity,
        contributingFactors: prediction.contributingFactors,
      },
      createdAt: Date.now(),
      acknowledged: false,
    };

    this.alerts.set(alertId, alert);
    this.notifyAlertCallbacks(alert);
  }

  /**
   * Generate predictive alerts
   */
  private generatePredictiveAlerts(): void {
    // Check for threshold breaches and anomalies
    // This would analyze current metrics and generate alerts
  }

  /**
   * Notify alert callbacks
   */
  private notifyAlertCallbacks(alert: PredictiveAlert): void {
    this.alertCallbacks.forEach((callback) => {
      try {
        callback(alert);
      } catch (error) {
        console.error('Error in alert callback:', error);
      }
    });
  }

  // Public API Methods

  /**
   * Subscribe to alerts
   */
  public onAlert(callback: (alert: PredictiveAlert) => void): () => void {
    this.alertCallbacks.push(callback);

    return () => {
      const index = this.alertCallbacks.indexOf(callback);
      if (index > -1) {
        this.alertCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Get current alerts
   */
  public getAlerts(): PredictiveAlert[] {
    return Array.from(this.alerts.values()).sort((a, b) => b.createdAt - a.createdAt);
  }

  /**
   * Get alerts by domain
   */
  public getAlertsByDomain(domain: string): PredictiveAlert[] {
    return this.getAlerts().filter((alert) => alert.domain === domain);
  }

  /**
   * Acknowledge alert
   */
  public acknowledgeAlert(alertId: string): void {
    const alert = this.alerts.get(alertId);
    if (alert) {
      alert.acknowledged = true;
    }
  }

  /**
   * Get patterns
   */
  public getPatterns(): SafetyPattern[] {
    return this.engine.getPatterns();
  }

  /**
   * Get metric history
   */
  public getMetricHistory(domain: string, type: string): SafetyMetric[] {
    return this.engine.getMetricHistory(domain, type);
  }

  /**
   * Get risk predictions
   */
  public getRiskPredictions(domain: string): RiskPrediction[] {
    return this.engine.generateRiskPredictions(domain);
  }

  /**
   * Get system statistics
   */
  public getSystemStats(): {
    totalAlerts: number;
    criticalAlerts: number;
    acknowledgedAlerts: number;
    activePredictions: number;
    patternsDetected: number;
    monitoringDomains: number;
  } {
    const alerts = this.getAlerts();
    const patterns = this.getPatterns();

    return {
      totalAlerts: alerts.length,
      criticalAlerts: alerts.filter((a) => a.severity === 'CRITICAL').length,
      acknowledgedAlerts: alerts.filter((a) => a.acknowledged).length,
      activePredictions: alerts.filter((a) => a.type === 'PREDICTIVE_WARNING').length,
      patternsDetected: patterns.length,
      monitoringDomains: 4, // HEALTHCARE, FINANCIAL, LEGAL, OPERATIONAL
    };
  }

  /**
   * Cleanup resources
   */
  public destroy(): void {
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
    }
    this.alertCallbacks = [];
    this.alerts.clear();
  }
}

// React Context for Predictive Safety Monitor
const PredictiveSafetyContext = createContext<PredictiveSafetyMonitor | null>(null);

export const usePredictiveSafety = () => {
  const context = useContext(PredictiveSafetyContext);
  if (!context) {
    throw new Error('usePredictiveSafety must be used within PredictiveSafetyProvider');
  }
  return context;
};

export const PredictiveSafetyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [monitor] = useState(() => new PredictiveSafetyMonitor());

  useEffect(() => {
    return () => {
      monitor.destroy();
    };
  }, [monitor]);

  return (
    <PredictiveSafetyContext.Provider value={monitor}>{children}</PredictiveSafetyContext.Provider>
  );
};

export default PredictiveSafetyMonitor;
